import { Action, createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState } from '@ngrx/entity/src/models';
import { createEntityAdapter } from '@ngrx/entity';

import { partition } from 'lodash-es';

import * as customInsightsActions from '../actions/live-custom-insights.actions';

import ICustomInsight = Microsoft.VideoIndexer.Live.Contracts.ICustomInsight;
import IInsightImage = Microsoft.VideoIndexer.Live.Contracts.IInsightImage;

export interface State extends EntityState<ICustomInsight> {
  isLoading: boolean;
  isLoaded: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isImageUploading: boolean;
  isImageLoading: boolean;
  isImageDeleting: boolean;
  imageData: string | null;
  lastCreatedInsightId: string | null;
  temporaryImageFile: File | null;
  error?: string;
}

function selectInsightId(insight: ICustomInsight): string {
  return insight.insightId || insight.id;
}

export const adapter: EntityAdapter<ICustomInsight> = createEntityAdapter<ICustomInsight>({
  selectId: selectInsightId
});

export const initialState: State = adapter.getInitialState({
  isLoading: false,
  isLoaded: false,
  isCreating: false,
  isDeleting: false,
  isImageUploading: false,
  isImageLoading: false,
  isImageDeleting: false,
  lastCreatedInsightId: null,
  imageData: null,
  temporaryImageFile: null,
  error: null
});

const liveCustomInsightsReducer = createReducer(
  initialState,

  on(customInsightsActions.listCustomInsights, state => {
    return adapter.removeAll({
      ...state,
      isLoaded: false,
      isLoading: false,
      error: null
    });
  }),
  on(customInsightsActions.listCustomInsightsSuccess, (state, { response }) => {
    return adapter.setAll(response.results || [], {
      ...state,
      isLoading: false,
      isLoaded: true,
      error: null
    });
  }),

  on(customInsightsActions.listCustomInsightsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  on(customInsightsActions.createCustomInsight, state => ({
    ...state,
    isCreating: true,
    error: null
  })),

  on(customInsightsActions.createCustomInsightSuccess, (state, { insight }) => {
    return adapter.addOne(insight, {
      ...state,
      isCreating: false,
      lastCreatedInsightId: insight.id,
      error: null
    });
  }),

  on(customInsightsActions.createCustomInsightFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    error
  })),

  on(customInsightsActions.updateCustomInsight, state => ({
    ...state,
    isCreating: true,
    error: null
  })),

  on(customInsightsActions.updateCustomInsightSuccess, (state, { insight }) => {
    return adapter.upsertOne(insight, {
      ...state,
      isCreating: false,
      error: null
    });
  }),

  on(customInsightsActions.updateCustomInsightFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    error
  })),

  on(customInsightsActions.deleteCustomInsights, state => ({
    ...state,
    isDeleting: true,
    error: null
  })),

  on(customInsightsActions.deleteCustomInsight, state => ({
    ...state,
    isDeleting: true,
    error: null
  })),

  on(customInsightsActions.deleteCustomInsightSuccess, (state, { insightId }) => {
    return adapter.removeOne(insightId, {
      ...state,
      isDeleting: false,
      error: null
    });
  }),

  on(customInsightsActions.deleteCustomInsightFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    error
  })),

  on(customInsightsActions.deleteCustomInsightsBatchSuccess, (state, { insightIds }) => {
    return adapter.removeMany(insightIds, {
      ...state,
      isDeleting: false,
      error: null
    });
  }),

  on(customInsightsActions.deleteCustomInsightsBatchFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    error
  })),

  on(customInsightsActions.uploadCustomInsightImage, state => ({
    ...state,
    isImageUploading: true,
    error: null
  })),

  on(customInsightsActions.uploadCustomInsightImageSuccess, state => ({
    ...state,
    isImageUploading: false,
    error: null
  })),

  on(customInsightsActions.uploadCustomInsightImageFailure, (state, { error }) => ({
    ...state,
    isImageUploading: false,
    error
  })),

  on(customInsightsActions.getCustomInsightImage, state => ({
    ...state,
    isImageLoading: true,
    error: null
  })),

  on(customInsightsActions.getCustomInsightImageSuccess, state => ({
    ...state,
    isImageLoading: false,
    error: null
  })),

  on(customInsightsActions.getCustomInsightImageFailure, (state, { error }) => ({
    ...state,
    isImageLoading: false,
    error
  })),

  on(customInsightsActions.deleteCustomInsightImage, state => ({
    ...state,
    isImageDeleting: true,
    error: null
  })),

  on(customInsightsActions.deleteCustomInsightImageSuccess, state => ({
    ...state,
    isImageDeleting: false,
    error: null
  })),

  on(customInsightsActions.deleteCustomInsightImageFailure, (state, { error }) => ({
    ...state,
    isImageDeleting: false,
    error
  })),

  on(customInsightsActions.setTemporaryImageFile, (state, { file }) => ({
    ...state,
    temporaryImageFile: file
  })),

  on(customInsightsActions.clearTemporaryImageFile, state => ({
    ...state,
    temporaryImageFile: null
  })),

  on(customInsightsActions.createCustomModel, state => ({
    ...state,
    isCreating: true,
    error: undefined
  })),

  on(customInsightsActions.createCustomModelSuccess, (state, { model }) =>
    adapter.addOne(model, {
      ...state,
      isCreating: false,
      lastCreatedInsightId: model.id
    })
  ),

  on(customInsightsActions.createCustomModelFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    error
  })),

  on(customInsightsActions.updateCustomModel, state => ({
    ...state,
    isCreating: true,
    error: undefined
  })),

  on(customInsightsActions.updateCustomModelSuccess, (state, { model }) => {
    return adapter.updateOne(
      { id: model.id, changes: model },
      {
        ...state,
        isCreating: false
      }
    );
  }),

  on(customInsightsActions.updateCustomModelFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    error
  })),

  on(customInsightsActions.uploadCustomModelImages, state => ({
    ...state,
    isImageUploading: true
  })),

  on(customInsightsActions.uploadCustomModelImagesComplete, (state, { modelId, successfulImages, failedImages }) => {
    const model = state.entities[modelId];
    if (!model) {
      return { ...state, isImageUploading: false };
    }

    const [positiveImages, negativeImages] = partition(successfulImages, i => i.positiveExample);

    const updatedModel: ICustomInsight = {
      ...model,
      prompt: {
        ...model.prompt,
        images: [...(model.prompt?.images ?? []), ...positiveImages]
      },
      negativePrompt: model.negativePrompt
        ? {
            ...model.negativePrompt,
            images: [...(model.negativePrompt?.images ?? []), ...negativeImages]
          }
        : undefined
    };

    return adapter.updateOne(
      { id: modelId, changes: updatedModel },
      {
        ...state,
        isImageUploading: false
      }
    );
  }),

  on(customInsightsActions.deleteCustomModelImages, state => ({
    ...state,
    isImageDeleting: true
  })),

  on(customInsightsActions.deleteCustomModelImagesComplete, (state, { modelId, deletedIds, failedIds }) => {
    const model = state.entities[modelId];
    if (!model) {
      return { ...state, isImageDeleting: false };
    }

    const removeImagesByIds = (arr?: IInsightImage[]) => (arr ?? []).filter(img => !deletedIds.includes(img.id));

    const updatedModel: ICustomInsight = {
      ...model,
      prompt: {
        ...model.prompt,
        images: removeImagesByIds(model.prompt?.images)
      },
      negativePrompt: model.negativePrompt
        ? {
            ...model.negativePrompt,
            images: removeImagesByIds(model.negativePrompt?.images)
          }
        : undefined
    };

    return adapter.updateOne(
      { id: modelId, changes: updatedModel },
      {
        ...state,
        isImageDeleting: false
      }
    );
  }),

  on(customInsightsActions.switchInsightTypeToImage, state => ({
    ...state,
    isCreating: true,
    error: null
  })),

  on(customInsightsActions.switchInsightTypeToImageSuccess, (state, { insight }) => {
    return adapter.upsertOne(insight, {
      ...state,
      isCreating: false,
      error: null
    });
  }),

  on(customInsightsActions.switchInsightTypeToImageFailure, (state, { error }) => ({
    ...state,
    isCreating: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return liveCustomInsightsReducer(state, action);
}
